import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import AthletesGroupsMaterial from '../Components/MainMenu/AthletesGroupsMaterial';
import { getAthletes, deleteAthlete } from "../db"; // Import Dexie.js functions
import LoadinPage from '../Components/LoadingPage'


const Athletes = () => {
    const { t } = useTranslation();
    const [athletes, setAthletes] = useState([]);
    const [athletesFiltered, setAthletesFiltered] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        fetchAthletes();
        const handleEventsUpdated = async (e) => {
            if (e.tables?.includes('athletes')) {
                fetchAthletes();
            }
        }

        window.addEventListener('serverSync', handleEventsUpdated);

        return () => {
            window.removeEventListener('serverSync', handleEventsUpdated);
        };
    }, []);

    const fetchAthletes = async () => {
        let data = await getAthletes();

        // Sort athletes by nickname or lastname + firstname
        data.sort((a, b) => {
            const nameA = (a.nickname || `${a.firstname} ${a.lastname}`).toLowerCase();
            const nameB = (b.nickname || `${b.firstname} ${b.lastname}`).toLowerCase();
            return nameA.localeCompare(nameB);
        });

        await setAthletes(data);
        doFilter(data); // pass the sorted data to filter
        setLoading(false);
    };

    const doFilter = async () => {
        if (searchQuery.trim() === "") {
            setAthletesFiltered(athletes);
        } else {
            setAthletesFiltered(
                athletes.filter((athlete) =>
                    (athlete.nickname && athlete.nickname.toLowerCase().includes(searchQuery)) ||
                    (athlete.firstname && athlete.firstname.toLowerCase().includes(searchQuery)) ||
                    (athlete.lastname && athlete.lastname.toLowerCase().includes(searchQuery))
                )
            );
        }
    }

    const handleSearch = (event) => {
        const query = event.target.value.toLowerCase();
        setSearchQuery(query);
        doFilter();
    };

    const handleDelete = async (id) => {
        if (window.confirm(t("athletes.removeConfirmation"))) {
            await deleteAthlete(id);
            fetchAthletes(); // Refresh the list after deletion
        }
    };

    useEffect(() => {
        doFilter();
    }, [searchQuery, athletes]);

    return (
        <div className="p-4 flex flex-col h-full relative">
            <AthletesGroupsMaterial headline={t("athletes.headline")} type="athletes" />
            <div className="grow overflow-y-auto overflow-x-hidden">
                {loading ? (
                    <LoadinPage />
                ) : athletes.length === 0 ? (
                    <div>
                        <div className="font-bold">{t("athletes.noAthleteFound")}</div>
                        <div className="mt-10 px-2">
                            <Link to="/athletes/new" className="button-primary">
                                <IconPlus className="w-3 h-auto" />
                                {t("athletes.addNew")}
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-6">
                        <div>
                            <div>
                                <label htmlFor="athleteSearch" className="form-input-wrap relative">
                                    <input id="athleteSearch"
                                           value={searchQuery}
                                           onChange={handleSearch}
                                           type="search" placeholder={t("athletes.searchFor")} className="!pr-8" />
                                    <IconSearch className="form-input-icon" stroke="3" />
                                </label>
                            </div>
                        </div>
                        {athletesFiltered.length > 0 ? (
                            <div className="flex flex-col gap-2 px-2 md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {athletesFiltered.map((athlete) => (
                                    <Link key={athlete.id} className="button-primary w-full text-left text-sm truncate" to={`/athletes/${athlete.id}`}>
                                        {athlete.nickname && (
                                            <span className="font-bold">{athlete.nickname}</span>
                                        )}
                                        {athlete.firstname} {athlete.lastname}
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div>
                                <div className="font-bold">{t("athletes.noAthleteFoundForFilter")}</div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <Link to="/athletes/new" className="p-4 rounded-full bg-[var(--dark-color)] text-[var(--light-color)] absolute right-2 bottom-2">
                <IconPlus className="w-6 h-auto" />
                <span className="sr-only">{t("athletes.addNew")}</span>
            </Link>
        </div>
    );
};

export default Athletes;